import { withErrorBoundary } from '@sentry/react';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { useMutation, useQuery, useSubscription } from 'urql';
import { assets } from '../../assets';
import { BlurredContainerM2030 } from '../../components/containers/containers';
import DefaultScreen from '../../components/containers/default-screen';
import InfiniteList from '../../components/lists/infinite-list';
import { Fallback } from '../../components/utils/error-boundary';
import { GraphqlOperations } from '../../logic/graphql/operations';
import { Notification, Query, Subscription } from '../../logic/graphql/types/types';
import NotificationCard from './notification-card';
import { FormattedMessage, useIntl } from 'react-intl';

const Container = styled(BlurredContainerM2030)`
  @media (max-width: 1280px) {
    padding: 20px 5px 10px 10px;
  }
`;

const Notifications = (): JSX.Element => {
  const intl = useIntl();
  const history = useHistory();

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);

  const result = useQuery<Query>({
    query: GraphqlOperations.queries.get_notifications.queryString,
    variables: GraphqlOperations.queries.get_notifications.createVars(currentPage, 10),
    requestPolicy: 'network-only',
  })[0];
  const { data, fetching } = result;

  useEffect(() => {
    if (
      data?.get_notifications?.page_count !== undefined &&
      data.get_notifications.page === currentPage
    ) {
      setNotifications([...notifications.concat(data.get_notifications.notifications)]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const readNotification = useMutation(
    GraphqlOperations.mutations.read_notification.mutationString,
  )[1];

  const handleSubscription = (
    subscriptionNotifications: Notification[] = [],
    response: Subscription,
  ): Notification[] => {
    if (response && response.new_notification) {
      setNotifications([response.new_notification].concat(notifications));
      return [response.new_notification, ...subscriptionNotifications];
    }
    return subscriptionNotifications;
  };

  useSubscription(
    {
      query: GraphqlOperations.subscriptions.new_notification.subscriptionString,
    },
    handleSubscription,
  );

  const handleReadAllClick = (): void => {
    const variables = GraphqlOperations.mutations.read_notification.createVars(0);
    readNotification(variables)
      .then((result) => {
        if (result && !result.error) {
          const notifs = [...notifications];
          notifs.forEach((n) => {
            n.unread = false;
          });
          setNotifications(notifs);
        }
        return true;
      })
      .catch((err) => console.log(err));
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars
  const fetchNextPage = (startIndex: number, stopIndex: number): any => {
    if (startIndex / 10 >= currentPage) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleReadClick = (notification: Notification): void => {
    const variables = GraphqlOperations.mutations.read_notification.createVars(notification.id);
    readNotification(variables)
      .then((result) => {
        if (result && !result.error) {
          const notifs = [...notifications];
          const notif = notifs.find((n) => n.id === notification.id);
          if (notif) {
            notif.unread = false;
          }
          setNotifications(notifs);
        }
        return true;
      })
      .catch((err) => console.log(err));
  };

  const handleBackClick = (): void => {
    history.goBack();
  };

  const hasNextPage =
    result.data?.get_notifications?.page_count !== undefined &&
    result.data?.get_notifications?.page !== undefined &&
    result.data.get_notifications.page_count > result.data.get_notifications.page;

  return (
    <DefaultScreen
      id="notifications_screen"
      background={assets.images.profile_bg}
      title={intl.formatMessage({ id: 'notificationsPage.title' })}
      onBackClick={handleBackClick}
      loading={false}
    >
      <Container>
        <header className="flex flex-col items-end mb-3">
          <span
            className="text-12 underline cursor-pointer mr-2 lg:mr-0"
            onClick={handleReadAllClick}
          >
            <b>
              <FormattedMessage id="notificationsPage.readAllButton" />
            </b>
          </span>
        </header>

        <InfiniteList
          hasNextPage={hasNextPage}
          isNextPageLoading={fetching}
          items={notifications}
          loadNextPage={fetchNextPage}
          onClickItem={handleReadClick}
          desktopItemHeight={104}
          mobileItemHeight={280}
          listItem={NotificationCard}
        />
      </Container>
    </DefaultScreen>
  );
};

export default withErrorBoundary(Notifications, { fallback: Fallback });
